import type { Rule } from '@mockd/shared';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CircleCheckIcon,
  CircleXIcon,
  CopyIcon,
  RefreshCwIcon,
  SquarePenIcon,
  Trash2Icon,
} from 'lucide-react';
import { flattenConditions } from '../../utils/conditions';
import { StatusBadge } from '../common/StatusBadge';

interface RuleListProps {
  rules: Rule[];
  onEdit: (rule: Rule) => void;
  onDelete: (ruleId: string) => void;
  onToggleActive: (rule: Rule) => void;
  onReorder: (ruleIds: string[]) => void;
  onDuplicate: (ruleId: string) => void;
  onResetSequence: (ruleId: string) => void;
  /** At the per-endpoint rule cap — duplicating would be rejected by the server. */
  atLimit?: boolean;
  /** Explanation shown on the disabled duplicate control when `atLimit`. */
  limitReason?: string;
  /** Read-only caller: list the rules with no controls to change them (#296). */
  readOnly?: boolean;
}

function getRuleBadges(rule: Rule) {
  const badges: { label: string; className: string }[] = [];

  // A single-step response shows its StatusBadge instead
  const stepCount = rule.response?.steps?.length ?? 0;
  if (stepCount > 1) {
    badges.push({ label: `${stepCount} steps`, className: 'badge-info' });
  }

  if (rule.actions?.length) {
    const types = [...new Set(rule.actions.map((a) => a.type))];
    const typeLabels: Record<string, string> = {
      http_request: 'HTTP',
      set_variable: 'Set Var',
      store_variable: 'Store Var',
    };
    for (const t of types) {
      badges.push({ label: typeLabels[t] || t, className: 'badge-warning' });
    }
  }

  const flatConditions = flattenConditions(rule.conditions);
  const randomCond = flatConditions.find((c) => c.target === 'random');
  if (randomCond?.value) {
    badges.push({ label: `${randomCond.value}%`, className: 'badge-accent' });
  }

  const condCount = flatConditions.filter((c) => c.target !== 'random').length;
  if (condCount > 0) {
    badges.push({
      label: `${condCount} condition${condCount !== 1 ? 's' : ''}`,
      className: 'badge-ghost',
    });
  }

  // Control-flow states that deviate from the default response-rule behavior
  if (rule.response && !rule.capturesResponse) {
    badges.push({ label: 'response off', className: 'badge-outline badge-warning' });
  }
  if (rule.response && rule.capturesResponse && !rule.terminal) {
    badges.push({ label: 'continues', className: 'badge-outline' });
  }
  // Terminal without a live response halts evaluation and forces the endpoint
  // default — flag it whether the response is absent or merely staged
  if (rule.terminal && !(rule.response && rule.capturesResponse)) {
    badges.push({ label: 'blocks', className: 'badge-outline badge-error' });
  }

  return badges;
}

export function RuleList({
  rules,
  onEdit,
  onDelete,
  onToggleActive,
  onReorder,
  onDuplicate,
  onResetSequence,
  atLimit = false,
  limitReason,
  readOnly = false,
}: RuleListProps) {
  const sortedRules = [...rules].sort((a, b) => a.priority - b.priority);

  if (sortedRules.length === 0) {
    return (
      <div className="text-center py-12 text-base-content/70">
        <p className="mb-2">No rules configured</p>
        {!readOnly && (
          <p className="text-sm">
            Create a rule to customize responses based on request matching
          </p>
        )}
      </div>
    );
  }

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const ids = sortedRules.map((r) => r.id);
    [ids[index - 1], ids[index]] = [ids[index], ids[index - 1]];
    onReorder(ids);
  };

  const handleMoveDown = (index: number) => {
    if (index >= sortedRules.length - 1) return;
    const ids = sortedRules.map((r) => r.id);
    [ids[index], ids[index + 1]] = [ids[index + 1], ids[index]];
    onReorder(ids);
  };

  return (
    <div className="divide-y divide-base-200">
      {sortedRules.map((rule, index) => {
        const badges = getRuleBadges(rule);
        const steps = rule.response?.steps ?? [];
        const hasSequence = steps.length > 1;

        return (
          <div
            key={rule.id}
            className={`px-4 py-3 ${!rule.isActive ? 'opacity-50' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Reorder arrows */}
                {!readOnly && (
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="btn btn-ghost btn-xs p-0 h-4 min-h-0 disabled:opacity-20 tooltip tooltip-right"
                      data-tip="Move up"
                      aria-label="Move up"
                    >
                      <ChevronUpIcon className="size-3.5" />
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === sortedRules.length - 1}
                      className="btn btn-ghost btn-xs p-0 h-4 min-h-0 disabled:opacity-20 tooltip tooltip-right"
                      data-tip="Move down"
                      aria-label="Move down"
                    >
                      <ChevronDownIcon className="size-3.5" />
                    </button>
                  </div>
                )}

                <span className="text-sm font-medium text-base-content">
                  {rule.name || 'Unnamed rule'}
                </span>

                {steps.length === 1 && <StatusBadge status={steps[0].statusCode} />}

                {badges.map((badge, i) => (
                  <span key={i} className={`badge ${badge.className} text-xs`}>
                    {badge.label}
                  </span>
                ))}
              </div>

              {!readOnly && (
                <div className="flex items-center gap-2">
                  {hasSequence && (
                    <button
                      onClick={() => onResetSequence(rule.id)}
                      className="btn btn-ghost btn-xs tooltip tooltip-left"
                      data-tip="Reset sequence position"
                      aria-label="Reset sequence position"
                    >
                      <RefreshCwIcon className="size-4" />
                    </button>
                  )}

                  <button
                    onClick={() => onToggleActive(rule)}
                    className={`btn btn-ghost btn-xs tooltip tooltip-left ${
                      rule.isActive ? 'text-success' : 'text-base-content/70'
                    }`}
                    data-tip={rule.isActive ? 'Disable rule' : 'Enable rule'}
                    aria-label={rule.isActive ? 'Disable rule' : 'Enable rule'}
                  >
                    {rule.isActive ? (
                      <CircleCheckIcon className="size-5" />
                    ) : (
                      <CircleXIcon className="size-5" />
                    )}
                  </button>

                  <button
                    onClick={() => onEdit(rule)}
                    className="btn btn-ghost btn-xs tooltip tooltip-left"
                    data-tip="Edit rule"
                    aria-label="Edit rule"
                  >
                    <SquarePenIcon className="size-5" />
                  </button>

                  <button
                    onClick={() => onDuplicate(rule.id)}
                    disabled={atLimit}
                    className="btn btn-ghost btn-xs tooltip tooltip-left"
                    data-tip={atLimit ? limitReason ?? 'Rule limit reached' : 'Duplicate rule'}
                    aria-label="Duplicate rule"
                  >
                    <CopyIcon className="size-5" />
                  </button>

                  <button
                    onClick={() => onDelete(rule.id)}
                    className="btn btn-ghost btn-xs text-error tooltip tooltip-left"
                    data-tip="Delete rule"
                    aria-label="Delete rule"
                  >
                    <Trash2Icon className="size-5" />
                  </button>
                </div>
              )}
            </div>

            {rule.description && (
              <div className={`mt-1 ${readOnly ? '' : 'ml-9'} text-xs text-base-content/70`}>
                {rule.description}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
